import AndroidIcon from "@mui/icons-material/Android";
import AppleIcon from "@mui/icons-material/Apple";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import Divider from "@mui/material/Divider";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import Typography from "@mui/material/Typography";
import androidQrPath from "../../../../assets/svg/android_zookeeper_mobile.svg";
import iosQrPath from "../../../../assets/svg/ios_zookeeper_mobile.svg";
import appImage from "../../../../assets/jpg/SH7.jpeg";
import { useTranslation } from "react-i18next";
import { useState, useRef, useEffect } from "react";

const STORAGE_KEY = "show_zookeeper_ad";

const features = ["Feature1", "Feature2", "Feature3"];

const links = {
  android:
    "https://play.google.com/store/apps/details?id=com.kilobyte.zookeeper&hl=en",
  ios: "https://apps.apple.com/hr/app/zookeeper/id6778837667",
};

export default function ZookeeperMobileAdDialog() {
  const [open, setOpen] = useState(true);
  const switchRef = useRef(null);
  const { t } = useTranslation();

  return (
    <>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        disableScrollLock
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            overflow: "hidden",
          },
        }}
      >
        <Box
          sx={{
            position: "relative",
            display: "flex",
            flexDirection: { xs: "column-reverse", md: "row" },
            minHeight: { md: 480 },
          }}
        >
          <IconButton
            onClick={() => setOpen(false)}
            aria-label="Close"
            data-testid="zookeeper-ad.close-button"
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              zIndex: 1,
              bgcolor: "background.paper",
              "&:hover": { bgcolor: "action.hover" },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>

          {/* Left: Content */}
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              px: { xs: 3, md: 6 },
              py: { xs: 4, md: 5 },
            }}
          >
            <Typography
              variant="h4"
              component="h2"
              gutterBottom
              sx={{ fontWeight: 700 }}
            >
              {t("ZookeeperAd.Title")}
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ mb: 3, maxWidth: 420 }}
            >
              {t("ZookeeperAd.Description")}
            </Typography>

            <Stack spacing={1.25} sx={{ mb: 4 }}>
              {features.map((feature) => (
                <Stack
                  key={feature}
                  direction="row"
                  spacing={1.5}
                  alignItems="center"
                >
                  <Box
                    sx={{
                      width: 20,
                      height: 20,
                      flexShrink: 0,
                      borderRadius: "50%",
                      bgcolor: "action.selected",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <CheckIcon sx={{ fontSize: 13 }} />
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {t(`${"ZookeeperAd."}${feature}`)}
                  </Typography>
                </Stack>
              ))}
            </Stack>

            <Divider sx={{ mb: 3 }} />

            <Typography
              variant="overline"
              color="text.secondary"
              sx={{ display: "block", mb: 1.5, letterSpacing: "0.08em" }}
            >
              {t("ZookeeperAd.Scan")}
            </Typography>
            <Stack direction="row" spacing={3} sx={{ mb: 4 }}>
              <QrCodeLink
                link={links.android}
                alt="Android QR Code"
                name="Google Play"
                src={androidQrPath}
                icon={<AndroidIcon sx={{ fontSize: 16 }} />}
              />
              <QrCodeLink
                link={links.ios}
                alt="iOS QR Code"
                name="App Store"
                src={iosQrPath}
                icon={<AppleIcon sx={{ fontSize: 16 }} />}
              />
            </Stack>
          </Box>

          <Box
            sx={{
              flex: { xs: "none", md: "0 0 42%" },
              height: { xs: 0, md: "auto" },
              width: "100%",
            }}
          >
            <Box
              component="img"
              src={appImage}
              alt="Zookeeper Mobile App Preview"
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                display: "block",
              }}
            />
          </Box>
        </Box>
      </Dialog>
    </>
  );
}

function QrCodeLink({ alt, link, name, src, icon }) {
  return (
    <Link
      href={link}
      target="_blank"
      rel="noopener noreferrer"
      underline="hover"
      color="inherit"
      sx={{ textAlign: "center", display: "inline-block" }}
    >
      <Box
        sx={{
          width: 128,
          height: 128,
          p: 1.25,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1,
          mb: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box
          component="img"
          src={src}
          alt={alt}
          sx={{ width: "100%", height: "100%", objectFit: "contain" }}
        />
      </Box>
      <Stack
        direction="row"
        spacing={0.5}
        alignItems="center"
        justifyContent="center"
      >
        {icon}
        <Typography variant="caption" color="text.secondary">
          {name}
        </Typography>
      </Stack>
    </Link>
  );
}
